import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import Danger from "components/Danger";

const UploadMultiImage = ({ defaultImages = [], setFieldValue, fieldName }) => {
  const [fileList, setFileList] = useState(defaultImages);
  const [previews, setPreviews] = useState([]);
  const [warningMessage, setWarningMessage] = useState("");
  const fileInputRef = useRef();

  useEffect(() => {
    const objectUrls = [];
    // Build preview URLs
    const previewSrcs = fileList
      ?.map((item) => {
        if (item instanceof File) {
          const url = URL.createObjectURL(item);
          objectUrls.push(url);
          return url;
        }
        if (typeof item === "string") {
          return item;
        }
        return null;
      })
      .filter((src) => src);

    setPreviews(previewSrcs);
    setFieldValue(fieldName, fileList);

    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fileList, fieldName, setFieldValue]);

  // Add new files to list (checks max count)
  const addFiles = (newFiles) => {
    const combined = [...fileList, ...newFiles];
    if (combined.length > 6) {
      setWarningMessage("You can only upload up to 6 images.");
    } else {
      setWarningMessage("");
      setFileList(combined);
    }
  };

  // Handlers
  const onDrop = (e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const onChange = (e) => {
    const files = Array.from(e.target.files);
    const tooBig = files.find((f) => f.size > 921600);
    if (tooBig) {
      setWarningMessage("File size exceeds 900 KB!");
    } else {
      addFiles(files);
    }
  };

  const handleDelete = (index, e) => {
    e.stopPropagation();
    setFileList((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };

  const openFileDialog = () => fileInputRef.current.click();

  return (
    <div>
      <div
        role="button"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={openFileDialog}
        style={{ border: "2px dashed grey", padding: 20, textAlign: "center", cursor: "pointer" }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFileDialog();
          }
        }}
      >
        Drag & drop images here, or click to select
        {previews.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: 16 }}>
            {previews.map((src, idx) => (
              <div key={src} style={{ position: "relative", margin: 8 }}>
                <img
                  src={src}
                  alt={`Preview ${idx}`}
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
                <DeleteIcon
                  onClick={(e) => handleDelete(idx, e)}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    cursor: "pointer",
                    width: 18,
                    height: 18,
                    color: "black",
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        multiple
        onChange={onChange}
        style={{ display: "none" }}
      />
      {warningMessage && <Danger validation={warningMessage} />}
    </div>
  );
};

UploadMultiImage.propTypes = {
  defaultImages: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)])
  ),
  setFieldValue: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
};

export default UploadMultiImage;
